class ImageService {
  public validateImageFile(file: File): boolean {
    if (file.size > 7 * 1024 * 1024) { // 7MB limit
      alert('Image size exceeds 7MB limit.');
      return false;
    }

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      alert('Only PNG and JPEG images are supported.');
      return false;
    }

    return true;
  }

  public async imageToBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          const b64 = reader.result.split(',')[1];
          resolve(b64);
        } else {
          reject(new Error('Failed to read image data'));
        }
      };
      reader.onerror = (e) => reject(new Error('Failed to read image data: ' + e));
      reader.readAsDataURL(blob);
    });
  }

  // Read image file as base64
  public async readFileAsBase64(file: File): Promise<string> {

    const b64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (!reader.result || typeof reader.result !== 'string') {
          reject('Failed to read image file');
          return;
        }

        const b64 = reader.result.split(',')[1];
        resolve(b64);
      };
      reader.onerror = () => reject('Failed to read image file');
      reader.readAsDataURL(file);
    });
    return b64;
  }

  public async getFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
    const files: File[] = [];

    let imageProcessed = false;
    try {
      // Method 1: Check clipboard items (works on most desktop browsers)
      const items = Array.from(dataTransfer.items);
      const imageItem = items.find(item => item.type.startsWith('image/'));

      if (imageItem) {
        const file = imageItem.getAsFile();
        if (file && imageService.validateImageFile(file)) {
          files.push(file);
          imageProcessed = true;
        }
      }

      // Method 2: Check clipboard files (Android fallback)
      if (!imageProcessed && dataTransfer.files && dataTransfer.files.length > 0) {
        const files = Array.from(dataTransfer.files);
        const imageFile = files.find(file => file.type.startsWith('image/'));
        
        if (imageFile && imageService.validateImageFile(imageFile)) {
          files.push(imageFile);
          imageProcessed = true;
        }
      }

      // Method 3: Modern Clipboard API fallback (for newer browsers)
      if (!imageProcessed && navigator.clipboard && navigator.clipboard.read) {
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const clipboardItem of clipboardItems) {
            for (const type of clipboardItem.types) {
              if (type.startsWith('image/')) {
                const blob = await clipboardItem.getType(type);
                // Convert blob to file
                const file = new File([blob], 'pasted-image.' + type.split('/')[1], { type });
                if (imageService.validateImageFile(file)) {
                  files.push(file);
                  imageProcessed = true;
                  break;
                }
              }
            }
            if (imageProcessed) break;
          }
        } catch (clipboardError) {
          // Clipboard API not supported or permission denied, continue with other methods
          console.log('Clipboard API not available:', clipboardError);
        }
      }

    } catch (error) {
      console.error('Error processing pasted image:', error);
      alert('Failed to process the pasted image.');
    }

    return files;
  }

  public detectImageTypeFast(base64: string): 'image/png' | 'image/jpeg' | 'image/unknown' {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const prefix = base64Data.substring(0, 12);
    const binary = atob(prefix);
    
    // PNG signature: 89 50 4E 47 (iVBORw in base64)
    if (binary.charCodeAt(0) === 0x89 && 
        binary.charCodeAt(1) === 0x50 && 
        binary.charCodeAt(2) === 0x4E && 
        binary.charCodeAt(3) === 0x47) {
      return 'image/png';
    }
    
    // JPEG signature: FF D8 FF
    if (binary.charCodeAt(0) === 0xFF && 
        binary.charCodeAt(1) === 0xD8 && 
        binary.charCodeAt(2) === 0xFF) {
      return 'image/jpeg';
    }
    
    return 'image/unknown';
  }
}
export const imageService = new ImageService();